import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff, AlertCircle } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === "superadmin") navigate("/admin");
      else if (user.role === "dept_admin") navigate("/dept-admin");
      else navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Left: Atmospheric panel */}
      <div style={{
        flex: 1, position: "relative", overflow: "hidden",
        background: "linear-gradient(135deg, #2D4A2F 0%, #3D6140 40%, #5C7A5E 70%, #7C9A7E 100%)",
        display: "flex", flexDirection: "column", justifyContent: "center", padding: "3rem"
      }}>
        <div style={{ position: "relative", zIndex: 2 }}>
          <div style={{
            width: "3rem", height: "3rem", borderRadius: "0.75rem",
            background: "rgba(255,255,255,0.15)", backdropFilter: "blur(4px)",
            border: "1px solid rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.5rem"
          }}>
            <span style={{ fontFamily: "Bricolage Grotesque", fontWeight: 700, fontSize: "1.25rem", color: "white" }}>R</span>
          </div>
          <h1 style={{
            fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600,
            fontSize: "2.5rem", color: "white", lineHeight: 1.2, marginBottom: "1rem"
          }}>
            ReconX
          </h1>
          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "1rem", maxWidth: "22rem", lineHeight: 1.6 }}>
            Enterprise-grade rule-based reconciliation. Match, transform, and validate financial data with precision.
          </p>
          <div style={{ marginTop: "3rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
            {["Exact match & composite key rules", "Regex transformation engine", "1:many aggregation matching", "Exportable reconciliation reports"].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <div style={{ width: "1.25rem", height: "1.25rem", borderRadius: "9999px", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ color: "white", fontSize: "0.6rem" }}>✓</span>
                </div>
                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.875rem" }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Decorative pattern */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.05 }}>
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} style={{
              position: "absolute", borderRadius: "9999px",
              border: "1px solid white",
              width: `${(i + 1) * 60}px`, height: `${(i + 1) * 60}px`,
              top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
            }} />
          ))}
        </div>
      </div>

      {/* Right: Login form */}
      <div style={{
        width: "480px", flexShrink: 0,
        background: "var(--bg-default)",
        display: "flex", flexDirection: "column", justifyContent: "center",
        padding: "3rem"
      }}>
        <div className="animate-in">
          <h2 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.75rem", color: "var(--text-primary)", marginBottom: "0.5rem" }}>
            Welcome back
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: "2rem" }}>
            Sign in to your ReconX account
          </p>

          {error && (
            <div data-testid="login-error" style={{
              background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem",
              padding: "0.75rem 1rem", marginBottom: "1.25rem",
              display: "flex", alignItems: "flex-start", gap: "0.5rem"
            }}>
              <AlertCircle size={16} style={{ color: "#DC2626", marginTop: "0.1rem", flexShrink: 0 }} />
              <p style={{ color: "#991B1B", fontSize: "0.875rem", margin: 0 }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.375rem" }}>
                Email address
              </label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="recon-input"
                style={{ width: "100%", boxSizing: "border-box" }}
              />
            </div>
            <div>
              <label style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.375rem" }}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="recon-input"
                  style={{ width: "100%", boxSizing: "border-box", paddingRight: "2.75rem" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-btn"
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{ width: "100%", padding: "0.625rem 1rem", fontSize: "0.9rem", marginTop: "0.5rem", opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p style={{ marginTop: "1.5rem", fontSize: "0.8rem", color: "var(--text-muted)", textAlign: "center" }}>
            Forgot your password? Contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
